import { useEffect, useState, useCallback } from 'react'
import { View, Text, TouchableOpacity, ScrollView, StyleSheet, RefreshControl } from 'react-native'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/AuthContext'
import { fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { SectionLabel } from '../../components/Leagues/ui'
import Ionicons from '@expo/vector-icons/Ionicons'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import RankBadge from '../../components/RankBadge'
import { Skeleton } from '../../components/Home/ui'
import SegmentedTabs from '../../components/SegmentedTabs'
import { ErrorState, StaleBanner } from '../../components/StateViews'
import ClassificaStagionale from '../../components/Leagues/ClassificaStagionale'
import HeadToHead from '../../components/Leagues/HeadToHead'
import BadgeSummaryCard from '../../components/Badges/BadgeSummaryCard'
import RivalrySection from '../../components/Rivalry/RivalrySection'
import { useLeagueFeatures } from '../../lib/useLeagueFeatures'
import ErrorBoundary from '../../components/ErrorBoundary'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

export default function Leagues() {
  const { t } = useTranslation()
  const router = useRouter()
  const tc = useThemeColors()
  const styles2 = makeStyles(tc)
  const { user, leagueId, leagueName, userLeagues } = useAuth()
  const insets = useSafeAreaInsets()

  const [stagionale, setStagionale] = useState([])
  const [membri, setMembri] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  // Schede: Classifica (tua posizione + stagionale), Sfide e Statistiche (confronto e rimando alla Bacheca: Albo, maglie, badge). Le statistiche si montano
  // solo alla prima apertura e poi restano montate (si conserva il giocatore scelto) ma nascoste.
  const [tab, setTab] = useState('classifica')
  const [statsVisited, setStatsVisited] = useState(false)
  const [rivalryVisited, setRivalryVisited] = useState(false)
  const features = useLeagueFeatures(leagueId)
  const selectTab = (k) => { if (k === 'stats') setStatsVisited(true); if (k === 'rivalry') setRivalryVisited(true); setTab(k) }

  // silent: aggiornamento (tira per ricaricare) senza svuotare la pagina
  const loadData = useCallback(async ({ silent = false } = {}) => {
    if (!leagueId) return
    if (!silent) setLoading(true)
    setError(false)
    try {
      const [sRes, mRes] = await Promise.all([
        supabase.rpc('fn_classifica_stagionale', { p_id_league: leagueId }),
        supabase.rpc('fn_league_members', { p_id_league: leagueId }),
      ])
      if (sRes.error || mRes.error) throw (sRes.error ?? mRes.error)
      setStagionale(sRes.data ?? [])
      setMembri(mRes.data ?? [])
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [leagueId])

  const onRefresh = useCallback(async () => {
    setRefreshing(true)
    await loadData({ silent: true })
    setRefreshing(false)
  }, [loadData])

  useEffect(() => { loadData() }, [loadData])

  const myPos = stagionale.find(s => s.out_id_user === user?.id)

  // distanza dagli altri: dal podio (se sono oltre il 3°) e dal giocatore subito sopra; se sono primo, il vantaggio sul 2°
  let myGap = null
  if (myPos) {
    const pts = (r) => Number(r?.punti_ranking ?? 0)
    if (myPos.posizione === 1) {
      const second = stagionale.find(s => s.posizione === 2)
      if (second) myGap = t('leagues.lead_over_next', { gap: pts(myPos) - pts(second), pos: 2 })
    } else {
      const prev = stagionale.find(s => s.posizione === myPos.posizione - 1)
      if (myPos.posizione > 3) {
        const third = stagionale.find(s => s.posizione === 3)
        if (third) myGap = t('leagues.gap_to_podium', { gap: Math.max(0, pts(third) - pts(myPos)) })
      } else if (prev) {
        myGap = t('leagues.gap_to_prev', { gap: Math.max(0, pts(prev) - pts(myPos)), pos: myPos.posizione - 1 })
      }
    }
  }

  return (
    <ScrollView
      style={[styles2.container, { backgroundColor: tc.bg }]}
      contentContainerStyle={{ paddingBottom: 40 + insets.bottom}}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          tintColor={tc.rosa}
          colors={[tc.rosa]}
        />
      }
    >
      {/* Header lega */}
      <View style={styles2.pageHeader}>
        <View>
          <SectionLabel>{leagueName}</SectionLabel>
          <Text style={[styles2.memberCount, { color: tc.textMuted }]}>
            {t('league.member_count', { count: membri.length })}
          </Text>
        </View>
        <TouchableOpacity
          style={[styles2.settingsBtn, { backgroundColor: tc.bgCard2, borderColor: tc.border }]}
          onPress={() => router.push('/Leagues/settings')}
          accessibilityRole="button"
          accessibilityLabel={t('leagues.settings')}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <Ionicons name="settings-outline" size={14} color={tc.textMuted} />
            <Text style={[styles2.settingsBtnText, { color: tc.textMuted }]}>{t('leagues.settings')}</Text>
          </View>
        </TouchableOpacity>
      </View>

      {/* Schede */}
      <SegmentedTabs
        options={[
          { key: 'classifica', label: t('leagues.tab_standings') },
          ...(features.rivalry ? [{ key: 'rivalry', label: t('leagues.tab_rivalry') }] : []),
          { key: 'stats', label: t('leagues.tab_stats') },
        ]}
        value={tab}
        onChange={selectTab}
        style={{ marginHorizontal: 16, marginBottom: 16 }}
      />

      {loading ? (
        <View style={{ paddingHorizontal: 16, gap: 12 }} accessibilityLabel={t('common.loading')}>
          <Skeleton height={150} radius={14} />
          <Skeleton height={90} radius={14} />
          <Skeleton height={90} radius={14} />
        </View>
      ) : error && stagionale.length === 0 && membri.length === 0 ? (
        <ErrorState onRetry={() => loadData()} message={t('leagues.load_error')} />
      ) : (
        <>
          {error && <View style={{ marginHorizontal: 16, marginBottom: 12 }}><StaleBanner onRetry={() => loadData({ silent: true })} /></View>}
          {/* Scheda Classifica */}
          <View style={{ display: tab === 'classifica' ? 'flex' : 'none' }}>
            {/* La mia posizione */}
            {myPos && (
              <View style={styles2.myPosWrap} accessible accessibilityLabel={`${t('leagues.your_position')}: ${myPos.posizione}, ${myPos.punti_ranking} pt${myGap ? `, ${myGap}` : ''}`}>
                <View style={{ flex: 1 }}>
                  <Text style={[styles2.myPosLabel, { color: tc.text }]}>{t('leagues.your_position')}</Text>
                  {!!myGap && <Text style={[styles2.myPosGap, { color: tc.textMuted }]}>{myGap}</Text>}
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                  <RankBadge pos={myPos.posizione} size={26} />
                  <Text style={[styles2.myPosPts, { color: tc.text }]}>{myPos.punti_ranking} pt</Text>
                </View>
              </View>
            )}

            {/* Lega con un solo membro: invito a portare gli amici */}
            {!loading && !error && membri.length === 1 && (
              <TouchableOpacity
                style={[styles2.inviteCard, { backgroundColor: tc.bgCard2, borderColor: tc.border }]}
                onPress={() => router.push('/Leagues/settings')}
                accessibilityRole="button"
                accessibilityLabel={t('leagues.invite_cta')}
              >
                <MaterialCommunityIcons name="account-multiple-plus-outline" size={26} color={tc.rosaText} />
                <View style={{ flex: 1 }}>
                  <Text style={[styles2.inviteTitle, { color: tc.text }]}>{t('leagues.invite_title')}</Text>
                  <Text style={[styles2.inviteBody, { color: tc.textMuted }]}>{t('leagues.invite_body')}</Text>
                </View>
                <Ionicons name="chevron-forward" size={16} color={tc.textMuted} />
              </TouchableOpacity>
            )}

            <ErrorBoundary label="ClassificaStagionale">
              <ClassificaStagionale stagionale={stagionale} currentUserId={user?.id} />
            </ErrorBoundary>
          </View>

          {/* Scheda Sfide */}
          {features.rivalry && rivalryVisited && user?.id && (
            <View style={{ display: tab === 'rivalry' ? 'flex' : 'none' }}>
              <ErrorBoundary label="RivalrySection">
                <RivalrySection leagueId={leagueId} userId={user.id} members={membri} />
              </ErrorBoundary>
            </View>
          )}

          {/* Scheda Statistiche */}
          {statsVisited && (
            <View style={{ display: tab === 'stats' ? 'flex' : 'none' }}>
              <ErrorBoundary label="HeadToHead">
                <HeadToHead stagionale={stagionale} leagueId={leagueId} />
              </ErrorBoundary>
              {user?.id && (
                <ErrorBoundary label="BadgeSummaryCard">
                  <BadgeSummaryCard leagueId={leagueId} userId={user.id} />
                </ErrorBoundary>
              )}
            </View>
          )}
        </>
      )}
    </ScrollView>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  container: { flex: 1, backgroundColor: tc.bg },
  pageHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, paddingBottom: 0, marginBottom: 20 },
  memberCount: { fontSize: 12, marginTop: 4, marginLeft: 13 },
  settingsBtn: { borderWidth: 1, borderRadius: 10, paddingHorizontal: 14, minHeight: 44, justifyContent: 'center' },
  settingsBtnText: { fontFamily: fonts.condensedBold, fontSize: 13, letterSpacing: 0.5 },
  myPosWrap: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: tc.rosaSubtle, borderWidth: 1, borderColor: tc.rosaBorder, borderRadius: 12, padding: 12, paddingHorizontal: 16, marginHorizontal: 16, marginBottom: 16 },
  myPosLabel: { fontSize: 13, fontWeight: '700' },
  myPosGap: { fontSize: 12, marginTop: 2 },
  myPosPts: { fontSize: 14, fontFamily: fonts.condensedBold },
  inviteCard: { flexDirection: 'row', alignItems: 'center', gap: 12, borderWidth: 1, borderRadius: 12, padding: 14, marginHorizontal: 16, marginBottom: 16, minHeight: 56 },
  inviteTitle: { fontFamily: fonts.condensedBold, fontSize: 16 },
  inviteBody: { fontSize: 12, marginTop: 2 },
  loading: { textAlign: 'center', padding: 40, fontSize: 13 },
})